import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, FileText, ChevronRight, Upload, Info, BadgeCheck } from "lucide-react";
import { CareShell } from "@/components/CareShell";
import { SpecialistProfileBody } from "@/components/CareSpecialist";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import api, { apiErrorMessage } from "@/lib/api";

function StepDots({ step }) {
  return (
    <div className="flex items-center gap-2 mb-6" aria-hidden>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={`h-1.5 rounded-full transition-all ${
            i <= step ? "bg-[#EF9F27] w-8" : "bg-[#333333] w-4"
          }`}
        />
      ))}
    </div>
  );
}

export default function NewReview() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [meta, setMeta] = useState({ specialties: [] });
  const [documents, setDocuments] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [profileSp, setProfileSp] = useState(null);

  const [title, setTitle] = useState("");
  const [specialty, setSpecialty] = useState(params.get("specialty") || "");
  const [concern, setConcern] = useState("");
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [specialistId, setSpecialistId] = useState(params.get("specialist") || "");

  useEffect(() => {
    Promise.all([api.get("/care/meta"), api.get("/documents")]).then(([m, d]) => {
      setMeta(m.data);
      setDocuments(d.data || []);
    });
  }, []);

  useEffect(() => {
    if (specialty) {
      api.get(`/care/specialists?specialty=${encodeURIComponent(specialty)}`).then(({ data }) => {
        const list = data && data.length ? data : [];
        setSpecialists(list);
      });
    }
  }, [specialty]);

  const toggleDoc = (id) =>
    setSelectedDocs((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const canNext =
    step === 0 ? title.trim() && specialty && concern.trim().length > 5 : true;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/care/cases", {
        title: title.trim(),
        concern: concern.trim(),
        specialty,
        document_ids: selectedDocs,
        preferred_specialist_id: specialistId || null,
      });
      toast.success("Case submitted — a specialist is reviewing it. We'll notify you when your opinion is ready.");
      navigate(`/care/cases/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(apiErrorMessage(e));
      setSubmitting(false);
    }
  };

  return (
    <CareShell active="new" title="New review" showBack onBack={() => (step > 0 ? setStep(step - 1) : navigate("/care"))}>
      <StepDots step={step} />

      {step === 0 && (
        <div data-testid="review-step-details">
          <h2 className="font-head text-2xl font-bold text-[#F8FAFC]">What's your concern?</h2>
          <p className="text-sm text-[#A1A1AA] mt-1 mb-5">
            Describe it in your own words — a specialist will review it.
          </p>

          <label className="block text-sm font-medium text-[#A1A1AA] mb-1.5">Title</label>
          <input
            data-testid="review-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Chest pain review"
            className="w-full bg-[#1A1A1A] border border-[#333333] rounded-xl px-4 py-3.5 min-h-[44px] text-[#F8FAFC] placeholder:text-[#666666] focus:outline-none focus:border-[#EF9F27] mb-4"
          />

          <label className="block text-sm font-medium text-[#A1A1AA] mb-1.5">Specialty</label>
          <select
            data-testid="review-specialty-select"
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value)}
            className="w-full bg-[#1A1A1A] border border-[#333333] rounded-xl px-4 py-3.5 min-h-[44px] text-[#F8FAFC] focus:outline-none focus:border-[#EF9F27] mb-4"
          >
            <option value="">Choose a specialty</option>
            {meta.specialties.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <label className="block text-sm font-medium text-[#A1A1AA] mb-1.5">Describe your concern</label>
          <textarea
            data-testid="review-concern-input"
            value={concern}
            onChange={(e) => setConcern(e.target.value)}
            rows={5}
            placeholder="Symptoms, when they started, what your local doctor has said so far…"
            className="w-full bg-[#1A1A1A] border border-[#333333] rounded-xl px-4 py-3.5 text-[#F8FAFC] placeholder:text-[#666666] focus:outline-none focus:border-[#EF9F27]"
          />
        </div>
      )}

      {step === 1 && (
        <div data-testid="review-step-records">
          <h2 className="font-head text-2xl font-bold text-[#F8FAFC]">Attach records</h2>
          <p className="text-sm text-[#A1A1AA] mt-1 mb-5">
            Add existing test results or reports (optional).
          </p>

          {documents.length === 0 ? (
            <div className="bg-[#1A1A1A] border border-dashed border-[#333333] rounded-2xl p-6 text-center">
              <Upload className="mx-auto text-[#666666]" />
              <p className="mt-2 text-[#A1A1AA] text-sm">No records yet.</p>
              <button
                data-testid="review-add-records-link"
                onClick={() => navigate("/care/records")}
                className="mt-3 text-sm font-bold text-[#EF9F27]"
              >
                Add a record first
              </button>
            </div>
          ) : (
            <ul className="space-y-3">
              {documents.map((d) => {
                const on = selectedDocs.includes(d.id);
                return (
                  <li key={d.id}>
                    <button
                      data-testid={`review-doc-${d.id}`}
                      onClick={() => toggleDoc(d.id)}
                      className={`w-full text-left rounded-2xl p-4 flex items-center gap-3 border transition-colors ${
                        on ? "bg-[#EF9F27]/10 border-[#EF9F27]" : "bg-[#1A1A1A] border-[#333333]"
                      }`}
                    >
                      <div
                        className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 border ${
                          on ? "bg-[#EF9F27] border-[#EF9F27] text-[#111111]" : "border-[#444444]"
                        }`}
                      >
                        {on && <Check size={16} />}
                      </div>
                      <FileText size={18} className="text-[#A1A1AA] shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-[#F8FAFC] truncate">{d.label}</p>
                        <p className="text-xs text-[#A1A1AA]">{d.category || d.file_type || "Document"}</p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {step === 2 && (
        <div data-testid="review-step-specialist">
          <h2 className="font-head text-2xl font-bold text-[#F8FAFC]">Choose a specialist</h2>
          <p className="text-sm text-[#A1A1AA] mt-1 mb-5">
            Optional — we'll assign the best match if you skip.
          </p>
          {specialists.length === 0 ? (
            <div className="bg-[#1A1A1A] border border-dashed border-[#333333] rounded-2xl p-6 text-center text-sm text-[#A1A1AA]">
              We'll match you with the best available specialist for this concern.
            </div>
          ) : (
          <ul className="space-y-3">
            {specialists.map((s) => {
              const on = specialistId === s.id;
              return (
                <li key={s.id}>
                  <button
                    data-testid={`review-specialist-${s.id}`}
                    onClick={() => setSpecialistId(on ? "" : s.id)}
                    className={`w-full text-left rounded-2xl p-4 border transition-colors ${
                      on ? "bg-[#EF9F27]/10 border-[#EF9F27]" : "bg-[#1A1A1A] border-[#333333]"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="font-head font-bold text-[#F8FAFC] flex items-center gap-1.5">
                          {s.name}
                          {s.verified && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[#22C55E] bg-[#22C55E]/15 px-1.5 py-0.5 rounded-full">
                              <BadgeCheck size={11} /> Verified
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-[#EF9F27]">{s.specialty}</p>
                        <p className="text-xs text-[#A1A1AA] mt-0.5">
                          {s.based}
                          {s.rating != null ? ` · ${s.rating}★` : (s.is_real ? " · New" : "")}
                          {s.years != null ? ` · ${s.years} yrs` : ""}
                          {s.fee != null ? ` · ${s.fee} ${s.currency || ""}` : ""}
                        </p>
                      </div>
                      {on && (
                        <div className="w-6 h-6 rounded-full bg-[#EF9F27] text-[#111111] flex items-center justify-center shrink-0">
                          <Check size={16} />
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-[#A1A1AA] mt-2">{s.bio}</p>
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid={`review-specialist-profile-${s.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setProfileSp(s);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          setProfileSp(s);
                        }
                      }}
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#EF9F27]"
                    >
                      <Info size={15} /> View full profile
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          )}
        </div>
      )}

      <div className="mt-8">
        {step < 2 ? (
          <button
            data-testid="review-next-btn"
            onClick={() => canNext && setStep(step + 1)}
            disabled={!canNext}
            className="w-full rounded-full bg-[#EF9F27] text-[#111111] font-bold px-6 py-3.5 min-h-[48px] flex items-center justify-center gap-2 transition-transform active:scale-[0.97] disabled:opacity-40"
          >
            Continue <ChevronRight size={18} />
          </button>
        ) : (
          <button
            data-testid="review-submit-btn"
            onClick={submit}
            disabled={submitting}
            className="w-full rounded-full bg-[#EF9F27] text-[#111111] font-bold px-6 py-3.5 min-h-[48px] flex items-center justify-center gap-2 transition-transform active:scale-[0.97] disabled:opacity-60"
          >
            {submitting ? "Submitting…" : "Submit for review"}
          </button>
        )}
      </div>

      <Dialog open={!!profileSp} onOpenChange={(o) => !o && setProfileSp(null)}>
        <DialogContent
          data-testid="specialist-profile-dialog"
          className="care-scope max-w-[380px] max-h-[85vh] overflow-y-auto no-scrollbar rounded-2xl bg-[#1A1A1A] border-[#333333] text-[#F8FAFC]"
        >
          <SpecialistProfileBody sp={profileSp} />
          {profileSp && (
            <button
              data-testid="specialist-profile-choose-btn"
              onClick={() => {
                setSpecialistId(profileSp.id);
                setProfileSp(null);
              }}
              className="mt-2 w-full rounded-full bg-[#EF9F27] text-[#111111] font-bold px-6 py-3 min-h-[44px] transition-transform active:scale-[0.97]"
            >
              Choose this specialist
            </button>
          )}
        </DialogContent>
      </Dialog>
    </CareShell>
  );
}
